import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';

export interface SubReportConfig {
  id: string;
  title: string;
  description?: string;
  columns: ColumnType<any>[];
  apiFunction?: string;
  hasNestedDrillDown?: boolean;
  nestedReportId?: string;
  clickableColumn?: string;
}

// Work Order Portfolio Drill Down Configuration
export const workOrderPortfolioDrillDownConfig: SubReportConfig = {
  id: 'work-order-portfolio-drilldown',
  title: 'Contractor Wise Summary',
  description: 'Detailed contractor-wise summary with budget and execution metrics',
  columns: [
    {
      title: 'Contractor Name',
      dataIndex: 'contractorName',
      key: 'contractorName',
      fixed: 'left' as const,
      width: 200,
    },
    {
      title: 'Budget',
      dataIndex: 'budgetAllocatedValue',
      key: 'budgetAllocatedValue',
      width: 150,
    },
    {
      title: 'Total Work Orders',
      dataIndex: 'totalWorkOrders',
      key: 'totalWorkOrders',
      width: 140,
      render: (text: any) => text,
    },
    {
      title: 'Works Executed',
      dataIndex: 'workExecutedValue',
      key: 'workExecutedValue',
      width: 150,
    },
    {
      title: 'Works Under Approval',
      dataIndex: 'workUnderExecutionValue',
      key: 'workUnderExecutionValue',
      width: 180,
    },
    {
      title: 'Approved RA Bills',
      dataIndex: 'approvedRABillsValue',
      key: 'approvedRABillsValue',
      width: 180,
    },
    {
      title: 'Payments Release',
      dataIndex: 'paymentsReleased',
      key: 'paymentsReleased',
      width: 150,
    },
    {
      title: 'Outstanding Value',
      dataIndex: 'outstandingValue',
      key: 'outstandingValue',
      width: 150,
    },
    {
      title: 'Over Due Value',
      dataIndex: 'overDueValue',
      key: 'overDueValue',
      width: 140,
    },
    {
      title: "Budget Variance",
      dataIndex: "budgetVarianceValue",
      key: "budgetVarianceValue",
      width: 220,
      render: (_: any, record: any) => {
        const value = record.budgetVarianceValue;
        const pct = record.budgetUtilizationPct;
        const text = pct || "";
        return (
          <span style={{ color: text.startsWith("+") ? "green" : "red" }}>
            {value} {pct && `(${pct})`}
          </span>
        );
      },
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      width: 150,
      render: (text: string) => (
        <span>{dayjs(text).format("DD-MMM-YYYY")}</span>
      ),
    },
  ],
  apiFunction: 'workOrderPortfolioDrillDown',
};

// Payments Released Summary - RA Bills Breakup Configuration
export const paymentsReleasedRABillsBreakupConfig: SubReportConfig = {
  id: 'payments-released-ra-bills-breakup',
  title: 'RA Bills Breakup',
  description: 'Detailed breakdown of RA bills',
  columns: [
    {
      title: 'WO',
      dataIndex: 'workOrderSequenceId',
      key: 'workOrderSequenceId',
      fixed: 'left' as const,
      width: 230,
    },
    {
      title: 'RA Bill',
      dataIndex: 'raBillSequenceId',
      key: 'raBillSequenceId',
      width: 230,
    },
    {
      title: 'RA Bills',
      dataIndex: 'raBillsApprovedValue',
      key: 'raBillsApprovedValue',
      width: 150,
    },
    {
      title: 'Payments Release',
      dataIndex: 'paymentCompletedValue',
      key: 'paymentCompletedValue',
      width: 150,
    },
    {
      title: 'Mode of Payment',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 150,
    },
    {
      title: 'Reference',
      dataIndex: 'referenceNumber',
      key: 'referenceNumber',
      width: 150,
    },
    {
      title: 'Outstanding Value',
      dataIndex: 'outstandingPaymentValue',
      key: 'outstandingPaymentValue',
      width: 150,
    },
    {
      title: 'Over Due Value',
      dataIndex: 'overduePaymentValue',
      key: 'overduePaymentValue',
      width: 150,
    },
    {
      title: 'Due Days',
      dataIndex: 'overDueDays',
      key: 'overDueDays',
      width: 120,
      render: (days: number) => (
        <span style={{ color: days > 30 ? 'red' : days > 15 ? 'orange' : 'green' }}>
          {days} days
        </span>
      ),
    },
  ],
  apiFunction: 'paymentsReleasedRABillsBreakup',
};

// Contractor Acceptance Tracker - WO Line Details Configuration
export const contractorAcceptanceWODetailsConfig: SubReportConfig = {
  id: 'contractor-acceptance-tracker-workOrder',
  title: 'Work Order Line Details',
  description: 'Detailed breakdown of work order lines',
  hasNestedDrillDown: true,
  nestedReportId: 'contractor-acceptance-baseline-summary',
  clickableColumn: 'workOrderLineId',
  columns: [
    {
      title: 'WO Line Number',
      dataIndex: 'workOrderLineId',
      key: 'workOrderLineId',
      fixed: 'left' as const,
      width: 120,
      render: (text: any, record: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#1890ff', cursor: 'pointer' }}>
              {text}
            </span>
          </div>
        );
      },
    },
    {
      title: 'WO #',
      dataIndex: 'workOrderId',
      key: 'workOrderId',
      width: 120,
    },
    {
      title: 'Task ID',
      dataIndex: 'taskTitle',
      key: 'taskTitle',
      width: 120,
    },
    {
      title: 'Work Line Amount',
      dataIndex: 'lineValue',
      key: 'lineValue',
      width: 120,
    },
  ],
  apiFunction: 'contractorAcceptanceWODetails',
};

// Contractor Acceptance Tracker - Baseline Summary Configuration
export const contractorAcceptanceWOlineSummaryConfig: SubReportConfig = {
  id: 'contractor-acceptance-baseline-summary',
  title: 'Baseline Summary Breakup',
  description: 'Baseline summary with detailed breakdowns',
  columns: [
    {
      title: 'Stage Title',
      dataIndex: 'stageTitle',
      key: 'stageTitle',
      fixed: 'left' as const,
      width: 150,
    },
    {
      title: 'Stage Description',
      dataIndex: 'stageDescription',
      key: 'stageDescription',
      width: 150,
    },
    {
      title: 'Split Percentage %',
      dataIndex: 'splitPercentage',
      key: 'splitPercentage',
      width: 150,
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      width: 150,
    },
    {
      title: 'Line Value',
      dataIndex: 'lineValue',
      key: 'lineValue',
      width: 150,
    },
  ],
  apiFunction: 'contractorAcceptanceWOLineDetails',
};

// Export all sub-report configurations
export const subReportConfigs = {
  'work-order-portfolio-drilldown': workOrderPortfolioDrillDownConfig,
  'payments-released-ra-bills-breakup': paymentsReleasedRABillsBreakupConfig,
  'contractor-acceptance-tracker-workOrder': contractorAcceptanceWODetailsConfig,
  'contractor-acceptance-baseline-summary': contractorAcceptanceWOlineSummaryConfig,
};

// Helper function to get sub-report configuration
export const getSubReportConfig = (id: string): SubReportConfig | undefined => {
  return subReportConfigs[id as keyof typeof subReportConfigs];
};